import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { CommonUtils } from 'src/utils/CommonUtils';
import { AppConstants } from 'src/environments/constants';
import { NonConformitaService } from 'src/app/services/non-conformita/non-conformita.service';
import { NonConformita } from 'src/app/model/nonConformita/NonConformitaModel';
import { StatoNonConformita } from 'src/app/model/nonConformita/NonConformita';

registerLocaleData(localeIt, 'it-IT');

@Component({
  selector: 'app-dashboard-non-conformita',
  templateUrl: './dashboard-non-conformita.component.html',
  styleUrls: ['./dashboard-non-conformita.component.scss'],
})
export class DashboardNonConformitaComponent implements OnInit, OnDestroy {
  private langChangeSub!: Subscription;
  private nonConformitaCache: NonConformita[] = [];
  nonConformitaDownloadComplete = false;
  nonConformitaPresenti = false;
  erroreGestitoMex: String = null;
  serverError: boolean = false;
  _isTeamView: boolean = false;

  private datePipe = new DatePipe('it-IT');

  tableConfig;

  emptyImage: string =
    sessionStorage.getItem('theme') === 'dark'
      ? '../../../assets/images/empty-state-dark.svg'
      : '../../../assets/images/empty-state-light.svg';

  private readonly tableHeaderKeys = [
    { text: 'nonConformita.codeNC', columnId: 'codiceNonConformita', width: '20%' },
    { text: 'ordini.supplier', columnId: 'fornitore', width: '40%' },
    { text: 'nonConformita.tipologia', columnId: 'tipologia', width: '20%' },
    { text: 'Dashboard.ncCreated', columnId: 'dataCreazione', width: '20%' },
    { text: 'ordini.state', columnId: 'stato', width: '30%' },
  ];

  @Input() set isTeamView(value: boolean) {
    if (!CommonUtils.isNotNullOrUndefined(this._isTeamView)) {
      this._isTeamView = value;
    } else if (value !== this._isTeamView) {
      this._isTeamView = value;
      this.caricaNonConformita();
    }
  }

  constructor(
    private nonConformitaServ: NonConformitaService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.caricaNonConformita();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      if (this.nonConformitaCache.length > 0) {
        this.buildTableConfig();
      }
    });
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  private buildTableConfig(): void {
    const keys = this.tableHeaderKeys.map(h => h.text);
    const translations = this.translate.instant(keys);

    this.tableConfig = {
      tableWidthPercentage: true,
      hasInfoHeader: false,
      tableAriaLabel: this.translate.instant('Dashboard.nonConformita'),
      tableHeader: {
        tableCellsHeader: this.tableHeaderKeys.map(h => ({
          text: translations[h.text] || h.text,
          columnId: h.columnId,
          columnWidth: h.width,
          textAlign: 'left',
        })),
        hiddenHeader: false,
        secondRightIconSticky: true,
      },
      tableRows: this.nonConformitaCache.map(nc =>
        this.mapNonConformitaToTableRow(nc)
      ),
      tooltipZindex: 4,
    };
  }

  caricaNonConformita() {
    this.nonConformitaDownloadComplete = false;
    this.nonConformitaPresenti = false;
    this.erroreGestitoMex = null;
    this.serverError = false;
    this.nonConformitaServ
      .getListNonConformita(
        this._isTeamView,
        AppConstants.DASHBOARD_FIRST,
        AppConstants.DASHBOARD_ORDINI_LENGHT
      )
      .subscribe(
        res => {
          if (res['errorMex'] != null) {
            this.erroreGestitoMex = res['errorMex'];
            console.log('Get Non Conformità error: ' + res['errorMex']);
          } else {
            let nonConformita = res['respObj'] || [];
            if (nonConformita.length > 0) {
              this.nonConformitaCache = nonConformita;
              this.buildTableConfig();
              this.nonConformitaPresenti = true;
            } else {
              this.nonConformitaCache = [];
              this.nonConformitaPresenti = false;
            }
          }
          this.nonConformitaDownloadComplete = true;
        },
        err => {
          this.serverError = true;
          console.error('Error catch', err);
          this.nonConformitaDownloadComplete = true;
        }
      );
  }

  private mapNonConformitaToTableRow(nc: NonConformita): any {
    const badge = this.getBadgeStatus(nc.stato);
    return {
      tableCells: [
        { type: 'text', text: nc.codice || '-' },
        { type: 'text', text: nc.fornitore?.ragioneSociale || '-' },
        {
          type: 'text',
          text: nc.tipologia
            ? this.translate.instant(nc.tipologia.toString())
            : '-',
        },
        {
          type: 'text',
          text: nc.dataCreazione
            ? this.datePipe.transform(nc.dataCreazione, 'dd.MM.yyyy') || '-'
            : '-',
        },
        {
          type: 'text-badge',
          text: nc.stato
            ? this.formatLabel(this.translate.instant(nc.stato.toString()))
            : '-',
          badgeStatus: badge.status,
          badgeCustomColor: badge.bkg,
        },
      ],
      secondRightIcon: 'icon-arrow-right',
      secondRightIconAriaLabel: 'Vai al dettaglio non conformità',
    };
  }

  formatLabel(segment) {
    return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
  }

   getBadgeStatus(element: StatoNonConformita): {
    status: any;
    bkg?: string;
  } {
    switch (element) {
      case StatoNonConformita.DA_CONFERMARE:
      case StatoNonConformita.DA_PRE_APPROVARE:
        return { status: 'custom', bkg: '#d6ae01' };
      case StatoNonConformita.APPROVATA:
        return { status: 'confirm' };
      case StatoNonConformita.RITARDO:
        return { status: 'error' };
      case StatoNonConformita.NUOVA:
        return { status: 'warning' };
      case StatoNonConformita.ELIMINATA:
        return { status: 'custom', bkg: 'var(--neutral-use-6)' };
      case StatoNonConformita.BOZZA:
        return { status: 'custom', bkg: 'var(--text-fill-primary)' };
      case StatoNonConformita.PRE_APPROVATA:
        return { status: 'custom', bkg: 'var(--charts-violet-1)' };
      default:
        return { status: 'neutral' };
    }
  }

  onSecondRightIconClick(event: any) {
    let rowIndex: number;
    if (
      typeof event?.detail?.row === 'string' &&
      event.detail.row.startsWith('row-')
    ) {
      rowIndex = Number.parseInt(event.detail.row.replace('row-', ''), 10);
    } else {
      rowIndex = event?.detail?.row;
    }

    const nc = this.nonConformitaCache[rowIndex];
    if (nc?.id) {
      this.router.navigate([AppConstants.NONCONFORMITA_ROUTE, nc.id]);
    }
  }
}
